/**
 * The hero at the top of every industry page, Ryerson and Southern Tool Steel: breadcrumb, title,
 * description paragraphs and a call-to-action button beside an image. From `lg` up the button sits
 * under the text; below `lg` it moves under the image and fills the width.
 *
 * Position: rendered by the Ryerson pages under `src/pages/metal-solutions/industries/` (all but
 * the index and the two redirect stubs) and by the dormant STS pages under
 * `src/pages/metal-solutions/industries-served/` (`automotive-transportation`, `heavy-equipment`,
 * `stamping`, `technology`, `tool-die`). Each passes one language of its content module's hero
 * object (`HeroContent` on the Ryerson pages, for example `TechnologyHeroContent` on STS), typed
 * with {@link HeroProps}. The industries landing page uses
 * `components/Solutions/Hero/Hero.tsx` instead.
 *
 * Two layouts: `isWatermarkHero` draws the section over `watermarkUrl` behind a brand-colour
 * gradient (every Ryerson industry page); otherwise it is a plain `ContentSection` (the STS pages).
 *
 * No video plays: a `hasVideo` hero draws `thumbnailUrl` as a still background image in the frame
 * the video player used to fill, and `heroVideoId` is not read.
 */
import React from "react";
import { ITheme, useTheme } from "@ryerson/frontend.theme";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { ContentSection } from "@ryerson/frontend.layout";
import { Grid, Row, Column } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { Button } from "@ryerson/frontend.button";
import { Media } from "@ryerson/frontend.next";
import { Breadcrumb } from "@ryerson/frontend.navigation";
import { VideoWrapperProps } from "@components/Helper/common";
import { getVideoPadding } from "@components/Helper/commonStyles";
import { Container } from "@ryerson/frontend.layout";
import rgba from "polished/lib/color/rgba";
import { navigate } from "@ryerson/frontend.next";
import Spacer from "@components/Shared/Spacer/Spacer";

/** Copy for one language of an industry hero; content modules type their objects with it. */
export interface HeroProps {
	/** The `h1`; also the image's `alt` text. */
	heroTitle: string;
	/** Paragraphs, one `Typography` each. */
	heroDescription: string[];
	buttonLabel: string;
	/**
	 * `true` shows the video frame (up to 560 × 360 px from `lg` up) filled with `thumbnailUrl`
	 * as a still image, or empty without one. `false` shows `heroImageUrl` as an `<img>`.
	 */
	hasVideo: boolean;
	/** Video id from when the hero played it with `@ryerson/frontend.media` → `Video`. Not read. */
	heroVideoId: string;
	/** Image shown when `hasVideo` is `false`. */
	heroImageUrl?: string;
	/** Still image shown in the video frame when `hasVideo` is `true`. */
	thumbnailUrl?: string;
	/** Draws the hero over `watermarkUrl` behind a brand-colour gradient. Default `false`. */
	isWatermarkHero?: boolean;
	/** Background image for `isWatermarkHero`. */
	watermarkUrl?: string;
	/** Where the button goes. Default `/contact-us`. */
	buttonUrl?: string;
}

const Divider = styled.div`
	width: 100%;
	opacity: 0.1;
	margin-top: 20px;
	margin-bottom: 50px;
	${(props: any) => {
		const { theme } = props;
		return css`
			border-bottom: 1px solid ${theme.colors.primary.white};
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				display: none;
			}
		`;
	}}
`;

const ButtonWrapper = styled.div`
	margin-top: 30px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-bottom: 0px;
			}
		`;
	}}
`;

const VideoWrapper = styled.div`
	${(props: VideoWrapperProps) => {
		const { theme, buttonOrientation = "right", buttonSize = "md" } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				aspect-ratio: 3/2.5;
				max-width: 100%;
				display: flex;
				justify-content: center;
				align-items: center;
				${buttonOrientation === "center"
					? 0
					: getVideoPadding(buttonOrientation, true, buttonSize)};
			}
			@media only screen and (min-width: ${theme.breakpoints.lg}) {
				max-width: 560px;
				height: 360px;
			}
			@media only screen and (max-width: ${theme.breakpoints.xl}) {
				max-width: ${buttonOrientation && buttonOrientation === "right"
					? "calc(100% - 40px)"
					: "560px"};
			}
		`;
	}}
`;

const ImageWrapper = styled.div`
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (min-width: ${theme.breakpoints.lg}) {
				max-width: 560px;
				height: 360px;
			}
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				height: 300px;
			}
		`;
	}}
`;
const HeroTitle = styled(Typography)`
	margin-top: 32px;
	margin-bottom: 43px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-top: 11px;
				margin-bottom: 13px;
			}
		`;
	}}
`;

const HeroText = styled.div`
	max-width: 358px;
	min-height: 242px;
	margin-bottom: 30px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-bottom: 0px;
				min-height: 0px;
				max-width: 100%;
				color: ${theme.colors.primary.lightGray};
			}
		`;
	}}
`;

// `font-${…}` below is not a valid CSS declaration (there is no colon), so browsers drop it; the
// breadcrumb's size comes from `Breadcrumb size="xs"`.
const BreadcrumbContainer = styled.div`
	margin-top: 32px;
	${(props: any) => {
		const { theme } = props;
		return css`
    font-${theme.typography.fonts.sizes.xs};
    @media only screen and (max-width: ${theme.breakpoints.lg}) {
        margin-top:8px;
    }
    `;
	}}
`;

/**
 * The still image that replaced the hero video: `thumbnailUrl` as a cover background, in the
 * frame the video used, including the padding `getVideoPadding` leaves below `lg` for a play
 * button.
 */
const VideoPlaceholder = styled.div`
	${(props: VideoWrapperProps & { thumbnailUrl?: string }) => {
		const { theme, buttonOrientation = "right", buttonSize = "md", thumbnailUrl } = props;
		return css`
			background-image: url(${thumbnailUrl});
			background-size: cover;
			background-position: center;
			background-repeat: no-repeat;
			width: 100%;
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				aspect-ratio: 3/2.5;
				width: 100%;
				max-width: 100%;
				${buttonOrientation === "center"
					? 0
					: getVideoPadding(buttonOrientation, true, buttonSize)};
			}
			@media only screen and (min-width: ${theme.breakpoints.lg}) {
				max-width: 560px;
				height: 360px;
			}
			@media only screen and (max-width: ${theme.breakpoints.xl}) {
				max-width: ${buttonOrientation && buttonOrientation === "right"
					? "calc(100% - 40px)"
					: "560px"};
			}
		`;
	}}
`;

const cssBottomSpacer = (theme: ITheme) => css`
	width: 100%;
	height: 120px;
	@media only screen and (max-width: ${theme.breakpoints.lg}) {
		height: 40px;
	}
	background-color: transparent;
`;

/** Renders an industry hero; see the file header and {@link HeroProps}. */
const Hero: React.FC<HeroProps> = ({
	heroTitle,
	heroDescription,
	buttonLabel,
	hasVideo,
	heroVideoId,
	heroImageUrl,
	thumbnailUrl,
	isWatermarkHero = false,
	watermarkUrl,
	buttonUrl = "/contact-us",
}) => {
	const { theme } = useTheme();

	const imageStyles = css`
		width: 100%;
		@media only screen and (max-width: ${theme.breakpoints.lg}) {
			height: 100%;
			object-fit: cover;
		}
	`;

	// The right-hand column: the video frame (`hasVideo`) or the image, plus the full-width button
	// below `lg`. `heroVideoId` is passed in but not used.
	const heroImageVideoContent = (
		hasVideo: boolean,
		heroVideoId: string,
		heroImageUrl?: string,
		thumbnailUrl?: string
	) => {
		if (hasVideo) {
			if (thumbnailUrl) {
				return (
					<>
						<Media greaterThanOrEqual="lg">
							<VideoWrapper theme={theme}>
								<VideoPlaceholder
									theme={theme}
									buttonOrientation="right"
									thumbnailUrl={thumbnailUrl}
								/>
							</VideoWrapper>
						</Media>
						<Media lessThan="lg">
							<VideoWrapper theme={theme}>
								<VideoPlaceholder
									theme={theme}
									buttonOrientation="top-right"
									thumbnailUrl={thumbnailUrl}
								/>
							</VideoWrapper>
							<ButtonWrapper theme={theme}>
								<Button
									label={buttonLabel}
									onClick={() => {
										navigate(buttonUrl);
									}}
									fullwidth={true}
								/>
							</ButtonWrapper>
						</Media>
					</>
				);
			} else {
				return (
					<>
						<Media greaterThanOrEqual="lg">
							<VideoWrapper theme={theme} buttonOrientation="right">
								<VideoPlaceholder theme={theme} buttonOrientation="right" />
							</VideoWrapper>
						</Media>
						<Media lessThan="lg">
							<VideoWrapper theme={theme} buttonOrientation="top-right">
								<VideoPlaceholder theme={theme} buttonOrientation="top-right" />
							</VideoWrapper>
							<ButtonWrapper theme={theme}>
								<Button
									label={buttonLabel}
									onClick={() => {
										navigate(buttonUrl);
									}}
									fullwidth={true}
								/>
							</ButtonWrapper>
						</Media>
					</>
				);
			}
		} else {
			return (
				<>
					<ImageWrapper theme={theme}>
						<img src={heroImageUrl} css={imageStyles} alt={heroTitle} />
					</ImageWrapper>
					<Media lessThan="lg">
						<ButtonWrapper theme={theme}>
							<Button
								label={buttonLabel}
								onClick={() => {
									navigate(buttonUrl);
								}}
								fullwidth={true}
							/>
						</ButtonWrapper>
					</Media>
				</>
			);
		}
	};

	if (isWatermarkHero) {
		return (
			<Container
				vPadding="0px"
				hPadding="0px"
				css={css`
					background: linear-gradient(
							180deg,
							${rgba(theme.colors.primary.secondaryBrand, 0.6)} 0%,
							${theme.colors.primary.secondaryBrand} 100%
						),
						url(${watermarkUrl});
					background-blend-mode: normal, luminosity;
					background-size: cover;
					background-repeat: no-repeat;
					background-position: center center;
					max-width: none;
				`}
			>
				<Container>
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<HeroTitle variant="h1" type="tertiary" theme={theme}>
						{heroTitle}
					</HeroTitle>
					<Divider theme={theme} />
					<Grid>
						<Row>
							<Column xs={12} lg={6}>
								<Media greaterThanOrEqual="lg">
									<HeroText theme={theme}>
										{heroDescription.map((description, index) => {
											return (
												<Typography
													variant="p"
													color={theme.colors.primary.white}
													key={index}
													size="md"
												>
													{description}
												</Typography>
											);
										})}
									</HeroText>
									<ButtonWrapper theme={theme}>
										<Button
											label={buttonLabel}
											onClick={() => {
												navigate(buttonUrl);
											}}
											size="lg"
										/>
									</ButtonWrapper>
								</Media>
								<Media lessThan="lg">
									<HeroText theme={theme}>
										{heroDescription.map((description, index) => {
											return (
												<Typography
													variant="p"
													type="tertiary"
													key={index}
													size="md"
												>
													{description}
												</Typography>
											);
										})}
									</HeroText>
								</Media>
							</Column>
							<Column xs={12} lg={6}>
								{heroImageVideoContent(
									hasVideo,
									heroVideoId,
									heroImageUrl,
									thumbnailUrl
								)}
							</Column>
						</Row>
					</Grid>
					<Spacer css={cssBottomSpacer(theme)} />
				</Container>
			</Container>
		);
	} else {
		return (
			<ContentSection type="tertiary">
				<BreadcrumbContainer theme={theme}>
					<Breadcrumb type="tertiary" size="xs" />
				</BreadcrumbContainer>
				<HeroTitle variant="h1" type="tertiary" theme={theme}>
					{heroTitle}
				</HeroTitle>
				<Divider theme={theme} />
				<Grid>
					<Row>
						<Column xs={12} lg={6}>
							<Media greaterThanOrEqual="lg">
								<HeroText theme={theme}>
									{heroDescription.map((description, index) => {
										return (
											<Typography
												variant="p"
												color={theme.colors.primary.white}
												key={index}
												size="md"
											>
												{description}
											</Typography>
										);
									})}
								</HeroText>
								<ButtonWrapper theme={theme}>
									<Button
										label={buttonLabel}
										onClick={() => {
											navigate(buttonUrl);
										}}
										size="lg"
									/>
								</ButtonWrapper>
							</Media>
							<Media lessThan="lg">
								<HeroText theme={theme}>
									{heroDescription.map((description, index) => {
										return (
											<Typography
												variant="p"
												type="tertiary"
												key={index}
												size="md"
											>
												{description}
											</Typography>
										);
									})}
								</HeroText>
							</Media>
						</Column>
						<Column xs={12} lg={6}>
							{heroImageVideoContent(
								hasVideo,
								heroVideoId,
								heroImageUrl,
								thumbnailUrl
							)}
						</Column>
					</Row>
				</Grid>
				<Spacer css={cssBottomSpacer(theme)} />
			</ContentSection>
		);
	}
};

export default Hero;
